<template>
    <section class="mb-8">
        <PageHeader class="gap-y-2 flex-col md:flex-row">
            <h1 ref="header">
                {{ $t("Search.MessageSearching") }}
                {{ globalConfig.SearchText }}
            </h1>
            <div class="hidden md:block grow"></div>
            <SearchDistributorLinks
                v-if="searchContext && searchContext.IsValidSearch && currentView == 'by-distributor'"
                :distributors />
        </PageHeader>
        <div v-if="isSearchInvalid" class="container">
            <SearchInvalid :result="invalidSearchResult" />
        </div>
        <div v-else class="container md:flex mt-4 gap-4">
            <div class="grow">
                <div class="flex flex-col md:flex-row justify-between items-center gap-4">
                    <SearchTabs>
                        <SearchTabsTrigger id="search-tab-by-part" value="by-part" content-id="search-parts">
                            {{ $t("Search.ByPart") }}
                        </SearchTabsTrigger>
                        <SearchTabsTrigger
                            id="search-tab-by-distributor"
                            value="by-distributor"
                            content-id="search-distributors">
                            {{ $t("Search.ByDistributor") }}
                        </SearchTabsTrigger>
                    </SearchTabs>
                    <Spinner v-if="isFetchingResults" />
                    <LazyInteractionSearchFilters :part-results :distributors :manufacturers hydrate-on-interaction />
                </div>
                <SearchTabsContent
                    id="search-distributors"
                    trigger-id="search-tab-by-distributor"
                    :selected="currentView == 'by-distributor'">
                    <SearchDistributors v-if="searchContext?.Results" :search-results="searchContext.Results" />
                </SearchTabsContent>
                <SearchTabsContent
                    id="search-parts"
                    trigger-id="search-tab-by-part"
                    :selected="currentView != 'by-distributor'">
                    <SearchParts v-if="searchContext?.Results" :search-results="searchContext.Results" />
                </SearchTabsContent>
                <Alert v-if="showNoResultsFiltered" variant="danger" class="my-8 max-w-xl">
                    <AlertDescription>{{ $t("Search.NoResultsFiltered") }}</AlertDescription>
                </Alert>
            </div>
            <SearchAds
                v-if="showAds"
                :key="searchContext?.SearchId"
                class="w-40 shrink-0"
                :google-ad-id="searchContext?.Results.GoogleAdId" />
        </div>
        <ClientOnly>
            <SearchReturnTop :header="header" />
        </ClientOnly>
    </section>
</template>
<script setup lang="ts">
import type { ShallowRef } from "vue";
import { useDocumentVisibility, useEventListener } from "@vueuse/core";

const { t } = useI18n();
const route = useRoute();
const globalConfig = useStateGlobalConfig();
const filterValues = useStateSearchFilters();
const { gtag } = useGtag();
const localePath = useLocalePath();
const { currentView, defaultView, setCurrentView } = useSearchView();
const { $api } = useNuxtApp();
const { toast } = useToast();
const { public: publicConfig } = useRuntimeConfig();
const breakpoints = useBreakpoints();
const { tokenState, generateToken, isExpired } = useSearchToken();
const documentVisibility = useDocumentVisibility();

const breakpointXL = breakpoints.greaterOrEqual("xl");
const breakpoint2XL = breakpoints.greaterOrEqual("2xl");
const isFetchingResults = ref(false);
const header = useTemplateRef("header");
const visibleDistributorIds = shallowRef<Set<number>>(new Set());
const visibleManufacturerIds = shallowRef<Set<number>>(new Set());
const visiblePartKeys = shallowRef<Set<string>>(new Set());
const visibleSortOrders = shallowRef<Set<string>>(new Set());
const searchContext = shallowRef<SearchContextTransformed | undefined>();
const invalidSearchResult = shallowRef<InvalidSearchResult | undefined>();

// search URL is either /search/{mfr}/{query} or /search/{query}
// Use the raw percent-encoded URL pathname rather than route.params to extract
// catch-all segments. h3 v1.15.7+ decodes path segments server-side as a security
// fix, causing '%23' to be treated as a fragment separator that truncates the search
// term (e.g. "LT8609SIV%23PBF" → "LT8609SIV" on the server), producing a hydration
// mismatch with the client. useRequestURL() preserves the intact percent-encoded URL
// (since h3 v1.15.10) on both server and client, so decoding here is consistent.
// We use route.params.path only to determine how many trailing segments belong to
// the catch-all — the count is correct even when the values are corrupted by h3.
const { pathname } = useRequestURL();
const routePath = route.params.path;
const paramCount = Array.isArray(routePath) ? routePath.length : routePath ? 1 : 0;
const path = paramCount > 0 ? pathname.split("/").filter(Boolean).slice(-paramCount).map(decodeURIComponent) : [];
const queryText = path[path.length - 1] ?? "";

// if query is empty redirect home
if (queryText.trim().length < 1) await navigateTo(localePath("/"));

// Generate a fresh token on first server-side render. The token is serialized
// into the SSR payload via useState, so the client picks it up without a
// second round-trip. The token is attached to /api/search* calls below.
if (import.meta.server) {
    await generateToken();
} else if (isExpired(publicConfig.searchTokenTtlMinutes)) {
    // Defense-in-depth: the token is stale for any client-side navigation that bypassed
    // useSearchNavigate (direct navigateTo, NuxtLink, etc.). navigateTo with external: true
    // aborts the setup pipeline, so the /api/search call below never executes with a stale token.
    await navigateTo(route.fullPath, { external: true });
}

const { data: searchResponse } = await useApi<SearchContext | InvalidSearchResult>("search", {
    query: {
        query: queryText,
        manufacturer: path.length > 1 ? path[0] : undefined,
        bot: route.query.bot === "true" ? true : undefined,
    },
    headers: tokenState.value ? { "X-Search-Token": tokenState.value.value } : {},
});

if (searchResponse.value?.IsValidSearch === true) {
    searchContext.value = transformSearchContext(searchResponse.value);
    searchResponse.value = undefined; // unset original
} else {
    invalidSearchResult.value = searchResponse.value;
}

const title = computed(() =>
    searchContext.value?.Manufacturer
        ? t("Search.SearchTitleManufacturerPartNumber", [searchContext.value.Manufacturer, queryText])
        : t("Search.SearchTitlePartNumber", [queryText])
);

const showAds = computed(() => breakpoint2XL.value || (currentView.value == "by-part" && breakpointXL.value));

const description = computed(() => t("Search.MetaDescriptionFormat", [queryText]));

const partResults = computed(() => searchContext.value?.Results?.PartResults ?? []);

const partDetailResults = computed(() => searchContext.value?.Results?.PartDetailResults ?? []);

const distributors = computed(() => searchContext.value?.Results?.Distributors ?? []);

const manufacturerMap = computed(() => searchContext.value?.Results?.ManufacturerMap ?? new Map());

const manufacturers = computed<PartResultManufacturer[]>(() => {
    return Array.from(manufacturerMap.value.values()).sort((manufacturerA, manufacturerB) =>
        manufacturerA.Name.localeCompare(manufacturerB.Name)
    );
});

const isSearchInvalid = computed(() => {
    if (invalidSearchResult.value) return true;
    if (partDetailResults.value.length < 1) return true;
    return false;
});

const isFiltering = computed(
    () =>
        filterValues.value.inStock ||
        filterValues.value.distributorIds.length > 0 ||
        filterValues.value.manufacturerIds.length > 0
);

const showNoResultsFiltered = computed(() => isFiltering.value && visiblePartKeys.value.size < 1);

/** Update a Set shallow ref with a new value only if it actually changed.
 * Since these refs are injected to potentially hundreds of child components,
 * this strategy avoids needlessly recomputing too much too often.
 */
function maybeUpdateSet<T>(setRef: ShallowRef<Set<T>>, newSet: Set<T>) {
    if (!areSetsEqual<T>(setRef.value, newSet)) setRef.value = newSet;
}

function updateVisibleIds() {
    const visible = calculateVisibleIds(partResults.value, filterValues.value);
    maybeUpdateSet(visibleDistributorIds, visible.distributorIds);
    maybeUpdateSet(visibleManufacturerIds, visible.manufacturerIds);
    maybeUpdateSet(visiblePartKeys, visible.partKeys);
    maybeUpdateSet(visibleSortOrders, visible.sortOrders);
}

function showHomeRegionWarning() {
    if (searchContext.value?.ShowHomeRegionWarning) {
        const canViewAmerica = searchContext.value.CanViewAmericasPricing;
        const homeRegionName = searchContext.value.HomeRegionName;
        const message = [
            t("Search.HomeRegionName", [homeRegionName]),
            t(canViewAmerica ? "Search.HomeRegionChina" : "Search.HomeRegionOther"),
        ].join(" ");
        toast({ description: message, variant: "warning" });
    }
}

function setTab(value: string | number) {
    setCurrentView(value as SearchView);
    gtag("event", "results_view_change", { results_view_name: value });
}

function fetchResults(): void {
    if (!searchContext.value?.IsValidSearch) return;
    isFetchingResults.value = true;
    $api<SearchResults>("search/results", {
        method: "GET",
        query: { id: searchContext.value.SearchId },
        headers: tokenState.value ? { "X-Search-Token": tokenState.value.value } : {},
    })
        .then((searchResults) => {
            if (!searchContext.value || !searchResults.IsComplete) return;

            // reassign because shallow ref
            searchContext.value = { ...searchContext.value, Results: transformSearchResults(searchResults) };
        })
        .finally(() => {
            isFetchingResults.value = false;
        });
}

// Reload the page when the user returns to this tab if the search token has expired.
// Covers two restoration paths:
//   1. visibilitychange – tab was backgrounded (not discarded); token expired while hidden.
//   2. pageshow (persisted) – browser restored the page from bfcache after a long suspension.
if (import.meta.client) {
    watch(documentVisibility, (state) => {
        if (state !== "visible") return;
        if (isExpired(publicConfig.searchTokenTtlMinutes)) {
            navigateTo(route.fullPath, { external: true });
        }
    });

    useEventListener(window, "pageshow", (event: PageTransitionEvent) => {
        if (!event.persisted) return;
        if (isExpired(publicConfig.searchTokenTtlMinutes)) {
            // Token is stale after a long bfcache suspension; force fresh SSR to generate a new token.
            navigateTo(route.fullPath, { external: true });
            return;
        }
        // The search/results fetch may have been cancelled when the page was frozen into bfcache.
        // isFetchingResults may be stuck true from the cancelled request, so reset it before retrying.
        if (!searchContext.value?.Results?.IsComplete) {
            isFetchingResults.value = false;
            fetchResults();
        }
    });
}

onMounted(() => {
    // Sync currentView from the cookie-backed defaultView. Covers two cases:
    // 1. Normal return visit: cookie was set server-side, currentView already matches (no-op).
    // 2. First visit after migration: the migration block in useSearchView wrote the
    //    localStorage value to the cookie but deliberately did not set currentView (to avoid
    //    a hydration mismatch). This assignment is where currentView is actually corrected.
    //    A one-time flash is acceptable here.
    currentView.value = defaultView.value;

    if (searchContext.value?.IsValidSearch) {
        showHomeRegionWarning();
        fetchResults();
    }
});

if (import.meta.server && publicConfig.enableAds) {
    useHead({
        script: [
            {
                src: "https://securepubads.g.doubleclick.net/tag/js/gpt.js",
                async: true,
                crossorigin: "anonymous",
                key: "gpt",
            },
        ],
    });
}

useSeoMeta({ title, ogTitle: title, description, ogDescription: description });

// reset filter values on each new search
filterValues.value = { inStock: false, manufacturerIds: [], distributorIds: [] };

if (invalidSearchResult.value?.Type == "Redirect") {
    await navigateTo(invalidSearchResult.value.URL, { replace: true }); // preserves back button navigation
}

globalConfig.value.SearchText = queryText;

if (searchContext.value?.IsValidSearch && searchContext.value.IsUndesirableSearchTerm) {
    useHead({
        meta: [{ name: "robots", content: "noindex" }],
    });
}

updateVisibleIds();

watch(partResults, updateVisibleIds);
watch(filterValues, updateVisibleIds, { deep: true });

provide(SearchIdsInjectKey, {
    distributorIds: visibleDistributorIds,
    manufacturerIds: visibleManufacturerIds,
    partKeys: visiblePartKeys,
    sortOrders: visibleSortOrders,
    clickHash: computed(() => (searchContext.value?.IsValidSearch ? searchContext.value.ClickHash : "")),
    searchId: computed(() => searchContext.value?.SearchId ?? ""),
});
provide(SearchInjectKey, { tab: readonly(currentView), setTab });
</script>
